import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import Logo from "../../assets/copysa.svg"; // Replace with the correct path
import Spinner from "../../components/Spinner";
import { useNavigate } from "react-router-dom";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const SignUp = () => {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [password] = useState("");
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();
  const [language, setLanguage] = useState(i18n.language);
  const isRtl = i18n.dir() === "rtl";
  const [anchorEl, setAnchorEl] = useState(null);

  // Regex for validating Iranian phone numbers
  const phoneRegex = /^(09|(\+98))\d{9}$/;

  const handleCheckboxChange = (event) => {
    setTermsAccepted(event.target.checked);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    if (!phoneRegex.test(phone)) {
      setError(t("Toast.phoneerr"));
      setLoading(false);
      return;
    }

    const signUpData = {
      phone,
      password,
    };

    try {
      const response = await fetch("https://api.copysa.ir/api/User/Register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(signUpData),
        credentials: "include",
      });

      if (response.ok) {
        setSuccess(true);
        navigate("/auth/login", { state: { phone } });
      } else {
        const errorText = await response.text(); // Get response text
        try {
          const errorData = JSON.parse(errorText); // Try parsing it as JSON
          setError(errorData.message || t("Toast.Regerr"));
        } catch (err) {
          // If response is not JSON, show the raw text
          setError(errorText || t("Toast.err"));
        }
      }
    } catch (err) {
      // Handle network errors
      setError(t("Toast.servererror"));
    } finally {
      setLoading(false);
    }
  };
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "fa", label: "فارسی" },
  ];
  useEffect(() => {
    const onLanguageChanged = (lng) => setLanguage(lng);
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);
  const handleLangSelect = (lang) => {
    i18n.changeLanguage(lang);
    document.cookie = `i18next=${lang}; path=/; domain=.copysa.ir; max-age=31536000`;

    setAnchorEl(null);
  };
  const isFormValid = phoneRegex.test(phone) && termsAccepted;

  return (
    <Box
      sx={{
        backgroundColor: "#F7F2FB",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Section */}
      <Box
        component="header"
        sx={{
          backgroundColor: "#512c63",
          width: "100%",
          paddingX: 2,
          paddingY: 3,
          mb:2,
          position: "sticky",
          top: 0,
          minHeight: "250px",
          zIndex: 10,
        }}
      >
        {/* Content container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: "1200px",
            margin: "0 auto",
            height: "100%",
            
          }}
        >
          {/* Left: Language Selector */}
          <Box
            sx={{
              width: "100%",
              mb: 5,
              display: "flex",
              justifyContent: "flex-start",
            }}
            dir="rtl"
          >
            <FormControl variant="outlined" size="small">
              <Select
                value={language}
                onChange={(e) => handleLangSelect(e.target.value)}
                sx={{
                  color: "white",
                  border: "1px solid white",
                  borderRadius: 2,
                  px: 2,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
                disableUnderline
                aria-label={t("aria.selectLanguage")}
              >
                {languageOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    aria-label={t("aria.selectLanguage", {
                      lang: option.label,
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontSize={20}>{option.icon}</Typography>
                      <Typography>{option.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Center: Logo */}
          <Box sx={{ textAlign: "center" }}>
            <img src={Logo} width={225} height={100} alt="copysa logo" />
          </Box>
          {/* Right: Placeholder for future elements */}
        </Box>

        {/* Wave Divider */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            lineHeight: 0,
            zIndex: -2,
          }}
        >
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            style={{
              display: "block",
              width: "100%",
              height: "50px",
              transform: "rotateY(180deg)",
              marginBottom: "-5px",
            }}
          >
            <path
              d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
              fill="#F7F2FB"
            />
          </svg>
        </Box>
      </Box>

      {/* Main Content Section */}
      <Box
        component="main"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: "2",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSignUp}
          sx={{
            color: "black",
            width: { xs: "100%", md: "30%" },
            display: "flex",
            flexDirection: "column",
            paddingX: "2rem",
          }}
        >
          <Typography
            textAlign="center"
            variant="h1"
            marginBottom={5}
            fontSize={28}
          >
            {t("Auth.SignUp")}
          </Typography>

          {/* Phone Number Field */}
          <TextField
            required
            name="number"
            value={phone}
            helperText={error}
            error={!!error}
            onChange={(e) => setPhone(e.target.value)}
            id="number"
            label={t("Auth.Phone")}
            variant="outlined"
            fullWidth
            sx={{ marginBottom: "1rem" }}
            InputLabelProps={{
              required: false,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneEnabledIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              autoComplete: "tel",
            }}
          />

          {/* Terms & Conditions */}
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={handleCheckboxChange}
                name="remember-me"
                id="remember-me"
              />
            }
            label={
              <Typography component="div" variant="body2">
                <span dir="rtl" style={{ display: "inline" }} className="ms-1">
                  {t("Term.Accept")}{" "}
                </span>
                <Link
                  to="/auth/agreement"
                  style={{ color: "#512c63", textDecoration: "underline" }}
                  dir="rtl"
                >
                  {t("Term.rules")}
                </Link>
              </Typography>
            }
          />

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={!isFormValid}
            variant="contained"
            color="primary"
            sx={{
              width: "100%",
              borderRadius: "8px",
              my: "1rem",
              padding: "12px",
              fontSize: "16px",
            }}
            size="large"
          >
            {t("Auth.SignUp")}
            {loading && <Spinner />}
          </Button>
        </Box>
      </Box>

      {/* Footer Section */}
      <Box
        component="footer"
        sx={{
          pb: "2rem",
          textAlign: "center",
          width: { xs: "86%", md: "30%" },
          marginX: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="h1">
          {t("Auth.acc")}
          <Link to="/auth/login" className="text-blue mx-1">
            {t("Auth.login")}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignUp;
